/*eslint-disable */
import type { ReactNode } from "react"
import React, { useCallback } from "react"
import BigNumber from "bignumber.js"

import type { MinimalToken, Token, TokenBalance } from "@/domains/core"
import { Button, ButtonVariantType } from "@/components/button"
import { Input } from "@/components/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/tooltip"
import { ChevronDownIcon } from "@/domains/icons"
import { cn, escapeRegExp, shortestValue } from "@/domains/library"

import { TokenIcon } from "./token-icon"

interface TokenInputProps {
  upper?: React.ReactNode
  center?: React.ReactNode
  lower?: React.ReactNode
  className?: string
}

export const TokenInput: React.FC<TokenInputProps> & {
  Upper: typeof TokenInputUpper
  Center: typeof TokenInputCenter
  Lower: typeof TokenInputLower
  Label: typeof TokenInputLabel
  Balance: typeof TokenInputBalance
  Value: typeof TokenInputValue
  Symbol: typeof TokenInputSymbol
  SelectableToken: typeof TokenInputSelectableToken
  PercentageButtons: typeof TokenPercentageButtons
  PercentageButton: typeof PercentageButton
  PercentageButtonBN: typeof PercentageButtonBN
} = ({ upper, center, lower, className }) => {
  return (
    <div className={cn("flex flex-col space-y-2", className)}>
      {upper}
      {center}
      {lower}
    </div>
  )
}

export const TokenInputUpper = ({ children, className }: { children: ReactNode; className?: string }) => {
  // Input labels, percentages and balance
  return (
    <div className={cn("flex flex-row flex-wrap items-center justify-between gap-y-2 text-xs", className)}>
      {children}
    </div>
  )
}

export const TokenInputCenter = ({ children, className }: { children: ReactNode; className?: string }) => {
  return (
    <div className={cn("border-shaded bg-shaded-dark overflow-hidden rounded-xl border-[1px] pr-2", className)}>
      <div className="flex flex-row items-center">{children}</div>
    </div>
  )
}

export const TokenInputLower = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <div className={className}>{children}</div>
}

export const TokenInputLabel = ({ label }: { label: string }) => {
  return (
    <div className="pr-2">
      <span className=" text-t200">{label}</span>
    </div>
  )
}

/**
 *
 * @deprecated Use the TokenInputBalance from the tokens domain
 * @returns
 */
export const TokenInputBalance = ({
  balance,
  decimals,
  label,
  minDecimals = 3,
  className,
}: {
  balance: string
  decimals: number
  label: React.ReactNode
  minDecimals?: number
  className?: string
}) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <span className={cn("flex items-center gap-1 text-sm", className)}>
          {label} {balance ? shortestValue({ value: balance, decimals, minDecimals }) : "0.00"}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-120">{balance}</PopoverContent>
    </Popover>
  )
}

/**
 * @deprecated Use the TokenInputValue from the tokens domain
 * @param param0
 * @returns
 */
export const TokenInputValue = ({
  symbol,
  value,
  decimals,
  onValueChange,
  onBlur,
  readOnly = false,
  className,
  disabled = false,
  placeholder = "0.0",
}: {
  symbol: string
  value: string
  decimals?: number
  onValueChange: (value: string) => void
  onBlur?: () => void
  readOnly?: boolean
  className?: string
  disabled?: boolean
  placeholder?: string
}) => {
  const handleInputChanged = useCallback(
    (e: any) => {
      if (!onValueChange) return
      const val = e.target.value
      if (val === "" || /^\d*(?:\\[.])?\d*$/.test(escapeRegExp(val))) {
        // lets make sure they are not typing more than the allowed decimals.
        const split = val.split(".")
        if (split.length > 1 && split[1].length > (decimals ?? 18)) {
          return
        }
        // if value is two chars long and the first char is a 0, only use the second char
        if (val.length === 2 && val[0] === "0" && val[1] !== ".") {
          onValueChange(val[1])
          return
        }
        onValueChange(val)
      }
    },
    [onValueChange],
  )
  return (
    <Input
      id={symbol}
      value={value}
      className={cn(
        "grow",
        "bg-transparent pl-4 text-lg leading-6 shadow-none",
        "rounded-xl rounded-l-none border-0 border-transparent",
        "ring-offset-shaded ring-0 ring-offset-0 ",
        "focus-within:border-0 focus-within:ring-0 focus:border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
        className,
      )}
      onChange={handleInputChanged}
      onBlur={onBlur}
      readOnly={readOnly}
      disabled={disabled}
      placeholder={placeholder}
    />
  )
}

/**
 * @important We have moved the TokenStore version of this to the tokens domain
 * @deprecated Use the TokenStore version from the tokens domain
 * @param param0
 * @returns
 */
export const TokenInputSymbol = ({
  token,
  className,
}: {
  token: MinimalToken
  className?: string
}) => {
  if (!token) {
    return null
  }

  return (
    <div className="flex flex-row items-center gap-1.5">
      <TokenIcon token={token} square={24} calledFrom="TokenInputSymbol" />
      <div className={cn("font-medium leading-6 text-t200", className)}>{token.symbol}</div>
    </div>
  )
}

export const TokenInputSelectableToken = ({ token }: { token: Token }) => {
  return (
    <div className="-mr-[2px] flex flex-none flex-row items-center text-t200">
      <TokenIcon token={token} square={24} calledFrom="TokenInputSelectableToken" />
      <div className="ml-[6px] mr-[2px] text-md font-medium ">{token.symbol}</div>
      <ChevronDownIcon size={16} />
    </div>
  )
}

/**
 * @deprecated Use the TokenPercentageButton from the tokens domain
 * @param param0
 * @returns
 */
const PercentageButton = ({
  className,
  disabled,
  hideTooltip = false,
  label,
  multiplier,
  onClick,
  size,
  token,
  tokenValue,
  variant,
}: {
  className?: string
  disabled?: boolean
  hideTooltip?: boolean
  label: string
  multiplier: number
  size?: ButtonVariantType["size"]
  token: MinimalToken & TokenBalance
  tokenValue?: string
  variant?: ButtonVariantType["variant"]
  onClick?: (value: string) => void
}) => {
  if (!token) {
    return null
  }

  const valueToUse = tokenValue ?? token.balance ?? 0

  const onClickValue = new BigNumber(valueToUse).multipliedBy(multiplier).toFixed(token.decimals)

  const button = (
    <Button
      className={className}
      variant={variant}
      size={size}
      onClick={() => {
        onClick?.(onClickValue)
      }}
      disabled={disabled}
    >
      {label}
    </Button>
  )
  if (hideTooltip) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom" className="border border-p400-light bg-p400 text-t200">
        <div>
          {onClickValue} {token.symbol}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * @deprecated Use the TokenPercentageButton from the tokens domain
 * @param param0
 * @returns
 */
const PercentageButtonBN = ({
  className,
  disabled,
  hideTooltip = false,
  label,
  onClick,
  size,
  token,
  amount,
  variant,
}: {
  className?: string
  disabled?: boolean
  hideTooltip?: boolean
  label: string
  size?: ButtonVariantType["size"]
  token: MinimalToken & TokenBalance
  amount: BigNumber
  variant?: ButtonVariantType["variant"]
  onClick?: (value: string) => void
}) => {
  if (!token) {
    return null
  }

  const button = (
    <Button
      className={className}
      variant={variant}
      size={size}
      onClick={() => {
        onClick?.(amount.toFixed(token.decimals))
      }}
      disabled={disabled}
    >
      {label}
    </Button>
  )
  if (hideTooltip) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom" className="border border-p400-light bg-p400 text-t200">
        <div>
          {amount.toFixed(token.decimals)} {token.symbol}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * @deprecated Use the TokenPercentageButtons from the tokens domain
 * @param param0
 * @returns
 */
export const TokenPercentageButtons: React.FC<{
  children: ReactNode
  className?: string
}> = ({ children, className }) => {
  return <div className={cn("flex flex-row items-center justify-end gap-1", className)}>{children}</div>
}

// Attach sub-components as properties of TokenInput
TokenInput.Upper = TokenInputUpper
TokenInput.Center = TokenInputCenter
TokenInput.Lower = TokenInputLower
TokenInput.Label = TokenInputLabel
TokenInput.Balance = TokenInputBalance
TokenInput.Value = TokenInputValue
TokenInput.Symbol = TokenInputSymbol
TokenInput.SelectableToken = TokenInputSelectableToken
TokenInput.PercentageButtons = TokenPercentageButtons
TokenInput.PercentageButton = PercentageButton
TokenInput.PercentageButtonBN = PercentageButtonBN
